/**
 * REST API Client
 * 
 * Axios-based client for Blue Clipboard REST API
 * - Automatic JWT token injection
 * - Token refresh on 401
 * - Error handling and logging
 * - Type-safe API methods
 * 
 * Requirements: 1.8, 2.1, 2.6, 2.7, 2.8, 4.1, 4.5, 5.1, 7.1, 8.1, 9.1
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getStoredTokens, refreshTokens, storeTokens, clearTokens, redirectToLogin } from '../auth/cognito';
import type {
  SkillConfig,
  CreateSkillConfigDto,
  UpdateSkillConfigDto,
  DeleteSkillConfigDto,
  DeleteSkillCategoryDto,
  SkillAssessment,
  CreateSkillAssessmentDto,
  ThresholdSettings,
  TimeTrialZoneResponse,
  SkillsReportResponse,
} from '../types/skills';
import type { Incident, CreateIncidentRequest, UpdateIncidentRequest } from '../types/incidents';
import type {
  TrainingPlan,
  TrainingPlansResponse,
  UpsertTrainingPlanDto,
  ImportTrainingPlansDto,
  TrainingPlanImportPreview,
  TrainingPlanImportResult,
  DeleteTrainingPlanDto,
  TrainingSeasonDatesDto,
} from '../types/trainingPlans';

// Cache helpers for users list (persists across page navigations)
const USERS_CACHE_KEY = 'blueclipboard_users_cache';
const USERS_CACHE_TIMESTAMP_KEY = 'blueclipboard_users_cache_timestamp';
const USERS_CACHE_VERSION_KEY = 'blueclipboard_users_cache_version';
const USERS_CACHE_VERSION = '6'; // Increment to invalidate old cache (v6: upgrade to localStorage)
const USERS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const USERS_RIDERS_CACHE_KEY = 'blueclipboard_users_riders_cache';
let usersFetchPromise: Promise<User[]> | null = null; // Deduplicate concurrent requests

// Storage helper: prefer localStorage, fall back to sessionStorage
function getStorage(): Storage {
    try {
        localStorage.setItem('__test__', '1');
        localStorage.removeItem('__test__');
        return localStorage;
    } catch {
        console.warn('[API] localStorage unavailable, falling back to sessionStorage');
        return sessionStorage;
    }
}

// Stable cache helpers for dashboard data (persists across page navigations)
const STABLE_CACHE_KEY = 'blueclipboard_stable_cache';
const STABLE_CACHE_TIMESTAMP_KEY = 'blueclipboard_stable_cache_timestamp';
const DASHBOARD_CACHE_VERSION_KEY = 'blueclipboard_dashboard_cache_version';
const DASHBOARD_CACHE_VERSION = '5'; // Increment to invalidate old cache (v5: lazy rider info, display_events)
const STABLE_CACHE_TTL = 30 * 60 * 1000; // 30 minutes
let dashboardFetchPromise: Promise<any> | null = null; // Deduplicate concurrent full dashboard requests
let rsvpFetchPromise: Promise<any> | null = null; // Deduplicate concurrent RSVP requests

// In-memory cache for time trials and race results (static data, 10-minute TTL)
const STATIC_DATA_CACHE_TTL = 10 * 60 * 1000; // 10 minutes
interface CacheEntry<T> { data: T; timestamp: number; }
const timeTrialsCache = new Map<string, CacheEntry<any[]>>();
const raceResultsCache = new Map<string, CacheEntry<any[]>>();
const timeTrialsInflight = new Map<string, Promise<any[]>>();
const raceResultsInflight = new Map<string, Promise<any[]>>();

function getCacheKey(params: Record<string, string | undefined>): string {
  return Object.entries(params)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join('&') || '__all__';
}

function getFromCache<T>(cache: Map<string, CacheEntry<T>>, key: string): T | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > STATIC_DATA_CACHE_TTL) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

// Helper functions for cache
function getUsersFromCache(): User[] | null {
  try {
    const storage = getStorage();
    const version = storage.getItem(USERS_CACHE_VERSION_KEY);
    if (version !== USERS_CACHE_VERSION) {
      // Cache version mismatch, clear old cache
      storage.removeItem(USERS_CACHE_KEY);
      storage.removeItem(USERS_CACHE_TIMESTAMP_KEY);
      storage.removeItem(USERS_RIDERS_CACHE_KEY);
      storage.setItem(USERS_CACHE_VERSION_KEY, USERS_CACHE_VERSION);
      return null;
    }
    
    const cached = storage.getItem(USERS_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

function getCacheTimestamp(): number | null {
  try {
    const storage = getStorage();
    const version = storage.getItem(USERS_CACHE_VERSION_KEY);
    if (version !== USERS_CACHE_VERSION) {
      return null;
    }
    
    const timestamp = storage.getItem(USERS_CACHE_TIMESTAMP_KEY);
    return timestamp ? parseInt(timestamp, 10) : null;
  } catch {
    return null;
  }
}

function setUsersCache(users: User[], riders?: any[]): void {
  try {
    const storage = getStorage();
    storage.setItem(USERS_CACHE_KEY, JSON.stringify(users));
    storage.setItem(USERS_CACHE_TIMESTAMP_KEY, Date.now().toString());
    storage.setItem(USERS_CACHE_VERSION_KEY, USERS_CACHE_VERSION);
    if (riders !== undefined) {
      storage.setItem(USERS_RIDERS_CACHE_KEY, JSON.stringify(riders));
    }
  } catch (err) {
    console.warn('[API] Failed to cache users:', err);
  }
}

function clearUsersCache(): void {
  try {
    const storage = getStorage();
    storage.removeItem(USERS_CACHE_KEY);
    storage.removeItem(USERS_CACHE_TIMESTAMP_KEY);
    storage.removeItem(USERS_RIDERS_CACHE_KEY);
    console.log('[API] Users cache cleared');
  } catch (err) {
    console.warn('[API] Failed to clear users cache:', err);
  }
}

// Stable cache helpers — use getStorage() (localStorage with sessionStorage fallback)
function getStableCacheFromStorage(): any | null {
  try {
    const storage = getStorage();
    const version = storage.getItem(DASHBOARD_CACHE_VERSION_KEY);
    if (version !== DASHBOARD_CACHE_VERSION) {
      // Cache version mismatch, clear old cache (including old v3 keys)
      storage.removeItem(STABLE_CACHE_KEY);
      storage.removeItem(STABLE_CACHE_TIMESTAMP_KEY);
      // Also clear old v3 keys for migration safety
      storage.removeItem('blueclipboard_dashboard_cache');
      storage.removeItem('blueclipboard_dashboard_cache_timestamp');
      storage.setItem(DASHBOARD_CACHE_VERSION_KEY, DASHBOARD_CACHE_VERSION);
      return null;
    }
    
    const cached = storage.getItem(STABLE_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

function getStableCacheTimestamp(): number | null {
  try {
    const storage = getStorage();
    const version = storage.getItem(DASHBOARD_CACHE_VERSION_KEY);
    if (version !== DASHBOARD_CACHE_VERSION) {
      return null;
    }
    
    const timestamp = storage.getItem(STABLE_CACHE_TIMESTAMP_KEY);
    return timestamp ? parseInt(timestamp, 10) : null;
  } catch {
    return null;
  }
}

function setStableCache(data: any): void {
  try {
    const storage = getStorage();
    storage.setItem(STABLE_CACHE_KEY, JSON.stringify(data));
    storage.setItem(STABLE_CACHE_TIMESTAMP_KEY, Date.now().toString());
    storage.setItem(DASHBOARD_CACHE_VERSION_KEY, DASHBOARD_CACHE_VERSION);
  } catch (err) {
    // Quota error on localStorage — fall back to sessionStorage
    console.warn('[API] Failed to cache dashboard in localStorage, falling back to sessionStorage:', err);
    try {
      sessionStorage.setItem(STABLE_CACHE_KEY, JSON.stringify(data));
      sessionStorage.setItem(STABLE_CACHE_TIMESTAMP_KEY, Date.now().toString());
      sessionStorage.setItem(DASHBOARD_CACHE_VERSION_KEY, DASHBOARD_CACHE_VERSION);
    } catch (fallbackErr) {
      console.warn('[API] Failed to cache dashboard in sessionStorage too:', fallbackErr);
    }
  }
}

function clearStableCache(): void {
  try {
    // Clear from both storages for migration safety (old entries may be in sessionStorage)
    localStorage.removeItem(STABLE_CACHE_KEY);
    localStorage.removeItem(STABLE_CACHE_TIMESTAMP_KEY);
    localStorage.removeItem(DASHBOARD_CACHE_VERSION_KEY);
    sessionStorage.removeItem(STABLE_CACHE_KEY);
    sessionStorage.removeItem(STABLE_CACHE_TIMESTAMP_KEY);
    sessionStorage.removeItem(DASHBOARD_CACHE_VERSION_KEY);
    // Also clear old v3 keys for migration safety
    localStorage.removeItem('blueclipboard_dashboard_cache');
    localStorage.removeItem('blueclipboard_dashboard_cache_timestamp');
    sessionStorage.removeItem('blueclipboard_dashboard_cache');
    sessionStorage.removeItem('blueclipboard_dashboard_cache_timestamp');
    console.log('[API] Stable cache cleared (both storages)');
  } catch (err) {
    console.warn('[API] Failed to clear stable cache:', err);
  }
}

// Stale cache helper: returns cached data regardless of TTL (for stale-while-error fallback)
function getStaleStableCache(): any | null {
  try {
    const storage = getStorage();
    const version = storage.getItem(DASHBOARD_CACHE_VERSION_KEY);
    if (version !== DASHBOARD_CACHE_VERSION) {
      return null;
    }

    const cached = storage.getItem(STABLE_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

// Offline error class
export class OfflineError extends Error {
  constructor(message: string = 'You are offline. This action requires an internet connection.') {
    super(message);
    this.name = 'OfflineError';
  }
}

// API Types
export interface Event {
  event_id: string;
  event_name: string;
  event_type: string;
  event_location: string;
  event_location_name?: string; // Human-readable location name (for display when event_location is UUID)
  event_address?: string;
  event_starttime: string;
  event_endtime: string;
  event_status?: 'active' | 'canceled'; // Optional - defaults to 'active' if not set
  event_checkin?: CheckinData | string; // Can be object or JSON string
  event_checkout?: Record<string, number> | string; // Can be object or JSON string
  event_expectedAttendance?: Record<string, number> | string; // Can be object or JSON string
  season: string;
  version?: number;
  trail_configuration_id?: string; // Optional - for Time Trial events
  official_results_url?: string; // Optional - for Race events, link to official results
  included_rider_ids?: string[]; // Optional - rider IDs invited to this event; absent = all riders invited
}

export interface CheckinData {
  riders: Record<string, number>;
  coaches: Record<string, number>;
  volunteers: Record<string, number>;
}

export interface CreateEventDto {
  event_id: string; // UUID generated on frontend
  event_name: string;
  event_type: string;
  event_location: string;
  event_address?: string;
  event_starttime: string;
  event_endtime: string;
  event_status: 'active' | 'canceled'; // Required for creation
  trail_configuration_id?: string; // Optional - for Time Trial events
  official_results_url?: string; // Optional - for Race events
  included_rider_ids?: string[]; // Optional - rider IDs invited to this event; absent = all riders invited
}

export interface UpdateEventDto extends Partial<CreateEventDto> {
  event_id: string;
  event_status?: 'active' | 'canceled';
  event_checkin?: CheckinData | string;
  event_checkout?: Record<string, number> | string;
  event_expectedAttendance?: Record<string, number> | string; // Can be object or JSON string
  official_results_url?: string; // Optional - for Race events
  included_rider_ids?: string[]; // Optional - rider IDs invited to this event; absent = all riders invited
  // trail_configuration_id inherited from Partial<CreateEventDto> - can be undefined to remove
}

export interface Rider {
  rider_id: string;
  name: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  category: string;
  group: string;
  grade?: string;
  level?: string;
  type?: 'Coach' | 'Rider' | 'Student';
  status?: 'active' | 'inactive' | 'graduated';
  disabled?: string | number; // Backend uses disabled field: "0" or "1"
  season: string;
  seasons_active?: string[]; // Array of all seasons this rider is active in
  associatedUsers?: string;
  birthday?: string; // ISO 8601 format: YYYY-MM-DD
  time_trials?: Array<{ date: string; result: string; trail_name?: string; direction?: string }>; // Historical time trial data
  race_registrations?: Record<string, boolean>;
}

export interface CreateRiderDto {
  name: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  category: string;
  group: string;
  grade?: string;
  level?: string;
  type?: 'Coach' | 'Rider' | 'Student';
  status?: 'active' | 'inactive' | 'graduated';
  season: string;
  associatedUsers?: string;
  birthday?: string; // ISO 8601 format: YYYY-MM-DD
  race_registrations?: Record<string, boolean>;
}

export interface UpdateRiderDto extends Partial<CreateRiderDto> {
  rider_id: string;
  seasons_active?: string[]; // Array of seasons to update
}

export interface User {
  username: string;
  email: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  phone?: string;
  groups: string[];
  last_login?: string;
  rider_associations?: Array<{
    rider_id: string;
    name: string;
    type?: string;  // Rider type (Coach/Student) for formatting
    role: string;
  }>;
  rider_display?: string;
}

export interface UserRiderAssociationInput {
  rider_id: string;
  role: string;
}

export interface CreateUserDto {
  email: string;
  given_name: string;   // First name (required)
  family_name: string;  // Last name (required)
  name?: string;        // Composed "First Last" (server is authoritative)
  phone?: string;
  groups?: string[];
  associations: UserRiderAssociationInput[]; // >=1 required at creation
}

export interface UpdateUserDto extends Partial<CreateUserDto> {
  username: string;
}

export interface LeaderboardEntry {
  rank: number;
  rider_id: string;
  name: string;
  type: 'Coach' | 'Rider' | 'Student'; // 'Rider' kept for backward compatibility
  events: number;
  hours: number;
}

export interface LeaderboardParams {
  season?: string;
  limit: number;
}

export interface RSVPLeaderboardEntry {
  rank: number;
  rider_id: string;
  name: string;
  type: 'Coach' | 'Rider' | 'Student';
  rsvp_count: number;
}

export interface WeeklyParticipationEntry {
  date: string;
  week_start: string;
  events: number;
  hours: number;
}

export interface EventTypeDistributionEntry {
  category: string;
  value: number;
}

export interface LeaderboardPageResponse {
  season: string;
  students_leaderboard: LeaderboardEntry[];
  coaches_leaderboard: LeaderboardEntry[];
  rsvp_leaderboard: RSVPLeaderboardEntry[];
  weekly_participation: WeeklyParticipationEntry[];
  event_type_distribution: EventTypeDistributionEntry[];
}

export interface WeeklyParticipationData {
  date: string;
  week_start: string;
  events: number;
  hours: number;
}

export interface EventTypeData {
  category: string;
  value: number;
}

export interface WeeklyAttendanceData {
  week: string;
  week_start: string;
  riders: number;
  coaches: number;
}

export interface StatisticsData {
  season: string;
  participation: WeeklyParticipationData[];
  eventTypes: EventTypeData[];
  attendance: WeeklyAttendanceData[];
}

export interface RiderStats {
  total_hours: number;
  event_count: number;
  time_trials?: TimeTrial[];
  race_results?: any[];
}

export interface TimeTrial {
  trial_id: string;
  rider_id: string;
  event_id: string;
  time: number;          // Time in seconds
  date: string;          // ISO format timestamp
  trail_name: string;
  direction: string;
  notes?: string;
  season: string;
}

export interface EventType {
  type_id: string;
  name: string;
  description?: string;
  color?: string;
}

export interface Location {
  location_id: string;
  name: string;
  address?: string;
}

export interface Group {
  group_id: string;
  name: string;
  description?: string;
  order?: number;
  group_leader?: string;
  group_skill_level?: string; // 'Beginner' | 'Intermediate' | 'Advanced' | '' (None)
}

export interface CheckinPageData {
  current_practice: Event | null;
  riders: Rider[];
  groups: Group[];
}

export interface TrailConfiguration {
  trail_id: string;
  trail_name: string;
  direction: string;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Create Axios instance with base configuration
 */
function createApiClient(): AxiosInstance {
  const baseURL = import.meta.env.VITE_API_BASE_URL;

  const client = axios.create({
    baseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - add JWT token to Authorization header
  // Requirements: 1.8, 11.7
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Check if offline and attempting write operation
      // Requirements: 11.7
      const isOffline = !navigator.onLine;
      const isWriteOperation = config.method && ['post', 'put', 'delete', 'patch'].includes(config.method.toLowerCase());
      
      if (isOffline && isWriteOperation) {
        throw new OfflineError();
      }

      const tokens = getStoredTokens();
      
      if (tokens && tokens.idToken) {
        config.headers.Authorization = `Bearer ${tokens.idToken}`;
      } else {
        console.warn('[API] No tokens found in storage');
      }

      // Log request in development
      if (import.meta.env.DEV) {
        console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle errors and token refresh
  // Requirements: 1.5, 1.6
  client.interceptors.response.use(
    (response) => {
      // Log response in development
      if (import.meta.env.DEV) {
        console.log(`[API] Response ${response.status} from ${response.config.url}`);
      }
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Handle 401 Unauthorized - attempt token refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        const tokens = getStoredTokens();
        if (tokens) {
          const newTokens = await refreshTokens(tokens);
          
          if (newTokens) {
            storeTokens(newTokens);
            
            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${newTokens.idToken}`;
            return client(originalRequest);
          }
        }

        // Token refresh failed - clear tokens and redirect to login
        clearTokens();
        redirectToLogin();
        return Promise.reject(error);
      }

      // Log error in development
      if (import.meta.env.DEV) {
        console.error(`[API] Error ${error.response?.status} from ${error.config?.url}:`, error.message);
      }

      return Promise.reject(error);
    }
  );

  return client;
}

// Create singleton instance
const apiClient = createApiClient();

/**
 * REST API Client Interface
 */
export const restClient = {
  // ============================================================================
  // Public — Self-service account recovery (U1)
  // Unauthenticated endpoint: must NOT use apiClient (which injects a JWT and
  // runs the auth-refresh interceptor). Uses a bare axios call instead.
  // Requirements: FR-2, FR-5
  // ============================================================================

  /**
   * Trigger self-service account recovery for the given email.
   * Public endpoint — always returns a neutral message regardless of account state.
   */
  async recoverAccess(email: string): Promise<{ message: string }> {
    const baseURL = import.meta.env.VITE_API_BASE_URL;
    console.log('[API] recoverAccess requested');
    const response = await axios.post<{ message: string }>(
      `${baseURL}/recover-access`,
      { email },
      { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
    );
    return response.data;
  },

  // ============================================================================
  // Events API
  // Requirements: 2.1, 2.6, 2.7, 2.8
  // ============================================================================

  /**
   * Get all events, optionally filtered by season
   */
  async getEvents(season?: string, userRiderIds?: string[]): Promise<Event[]> {
    const params: Record<string, string> = {};
    if (season) params.season = season;
    if (userRiderIds && userRiderIds.length > 0) {
      params.user_rider_ids = userRiderIds.join(',');
    }
    const response = await apiClient.get<Event[]>('/events', { params });
    return response.data;
  },

  /**
   * Get a single event by ID
   */
  async getEvent(id: string): Promise<Event> {
    const response = await apiClient.get<Event>(`/events/${id}`);
    return response.data;
  },

  /**
   * Create a new event
   */
  async createEvent(event: CreateEventDto): Promise<Event> {
    const response = await apiClient.post<Event>('/events', event);
    clearStableCache(); // Invalidate stable cache after creating
    return response.data;
  },

  /**
   * Update an existing event
   * 
   * Note: Legacy API uses PUT /events (not /events/{id})
   * The event_id is included in the request body
   */
  async updateEvent(
    _id: string,
    event: UpdateEventDto,
    options?: { resetRsvps?: boolean }
  ): Promise<Event> {
    // `reset_rsvps` is a transient COMMAND, not an event field. It is added to
    // the request body here and never to the DTO itself, because the store
    // optimistically merges the DTO into cached Event objects -- a flag placed
    // in the DTO would be spliced onto every cached event as a phantom
    // attribute. (Property P-4.) The backend likewise keeps it out of its
    // UPDATABLE_FIELDS allow-list so it is never persisted.
    const body = options?.resetRsvps ? { ...event, reset_rsvps: true } : event;
    const response = await apiClient.put<Event>('/events', body);
    clearStableCache(); // Invalidate stable cache after updating
    return response.data;
  },

  /**
   * Send "event updated, please RSVP" notification emails for an event.
   *
   * Admin only (enforced server-side). Returns FLAT counts -- note this differs
   * from sendCancelNotification, which nests them under `email`. Use
   * describeUpdateNotificationOutcome (not describeNotificationOutcome) to
   * interpret the result, or failures will read as successes.
   *
   * @param eventId - Event ID
   */
  async sendUpdateNotification(eventId: string): Promise<{
    message: string;
    emails_sent: number;
    errors: number;
    total_users: number;
  }> {
    console.log(`[API] sendUpdateNotification called for event: ${eventId}`);
    const response = await apiClient.post(
      `/events/${eventId}/update-notification`,
      {}
    );
    console.log('[API] Update notification request completed');
    return response.data;
  },

  /**
   * Delete an event
   */
  async deleteEvent(id: string): Promise<void> {
    await apiClient.delete(`/events/${id}`);
    clearStableCache(); // Invalidate stable cache after deleting
  },

  /**
   * Send event cancellation notifications (email + GroupMe)
   * 
   * @param eventId - Event ID
   * @param cancellationReason - Optional reason for cancellation
   * @returns Promise with notification results
   */
  async sendCancelNotification(
    eventId: string, 
    cancellationReason?: string
  ): Promise<{ 
    message: string; 
    email: { emails_sent: number; errors: number; total_users: number }; 
    groupme: { success: boolean; message?: string; error?: string } 
  }> {
    console.log(`[API] sendCancelNotification called for event: ${eventId}`);
    
    const body = cancellationReason ? { cancellation_reason: cancellationReason } : {};
    
    const response = await apiClient.post(
      `/events/${eventId}/cancel-notification`,
      body
    );
    
    console.log('[API] Cancel notification sent successfully');
    return response.data;
  },

  // ============================================================================
  // Riders API
  // Requirements: 4.1, 4.5
  // ============================================================================

  /**
   * Invalidate riders cache
   * Call this after creating, updating, or deleting riders
   */
  invalidateRidersCache(): void {
    try {
      // Clear all riders cache entries (all seasons)
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith('riders_cache_')) {
          sessionStorage.removeItem(key);
        }
      });
      console.log('[API] Riders cache invalidated');
    } catch (e) {
      // Ignore errors
    }
  },

  /**
   * Get all riders, optionally filtered by season
   * Results are cached for 2 minutes to improve performance on reports pages
   */
  async getRiders(season?: string): Promise<Rider[]> {
    // Cache key includes season parameter
    const cacheKey = `riders_cache_${season || 'all'}`;
    const timestampKey = `riders_cache_timestamp_${season || 'all'}`;
    const RIDERS_CACHE_TTL = 2 * 60 * 1000; // 2 minutes

    // Check cache first
    try {
      const cached = sessionStorage.getItem(cacheKey);
      const timestamp = sessionStorage.getItem(timestampKey);
      
      if (cached && timestamp) {
        const cacheAge = Date.now() - parseInt(timestamp);
        if (cacheAge < RIDERS_CACHE_TTL) {
          console.log('[API] Returning cached riders:', JSON.parse(cached).length, 'riders');
          return JSON.parse(cached);
        }
      }
    } catch (e) {
      // Ignore cache errors
    }

    // Fetch from API
    const params = season ? { season } : {};
    const response = await apiClient.get<Rider[]>('/riders', { params });
    
    // Cache the results
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
      sessionStorage.setItem(timestampKey, Date.now().toString());
      console.log('[API] Riders cached:', response.data.length, 'riders');
    } catch (e) {
      // Ignore cache errors (quota exceeded, etc.)
    }
    
    return response.data;
  },

  /**
   * Get a single rider by ID
   */
  async getRider(id: string): Promise<Rider> {
    const response = await apiClient.get<Rider>('/riders', {
      params: { rider_id: id }
    });
    return response.data;
  },

  /**
   * Create a new rider
   */
  async createRider(rider: CreateRiderDto): Promise<Rider> {
    const response = await apiClient.post<Rider>('/riders', rider);
    this.invalidateRidersCache(); // Invalidate cache after creating
    clearStableCache(); // Invalidate stable cache after creating
    clearUsersCache(); // Invalidate users cache (rider associations changed)
    return response.data;
  },

  /**
   * Update an existing rider
   * 
   * Note: Legacy API uses PUT /riders (not /riders/{id})
   * The rider_id is included in the request body
   */
  async updateRider(_id: string, rider: UpdateRiderDto): Promise<Rider> {
    const response = await apiClient.put<Rider>('/riders', rider);
    this.invalidateRidersCache(); // Invalidate cache after updating
    clearStableCache(); // Invalidate stable cache after updating
    clearUsersCache(); // Invalidate users cache (rider associations changed)
    return response.data;
  },

  /**
   * Delete a rider
   */
  async deleteRider(id: string): Promise<void> {
    await apiClient.delete('/riders', {
      data: { rider_id: id }
    });
    this.invalidateRidersCache(); // Invalidate cache after deleting
    clearStableCache(); // Invalidate stable cache after deleting
    clearUsersCache(); // Invalidate users cache (rider associations changed)
  },

  /**
   * Get riders admin data (includes active_riders and users_minimal)
   * Used for admin reports
   */
  async getRidersAdmin(): Promise<{ active_riders: Rider[]; users_minimal: User[]; groups?: any[] }> {
    const response = await apiClient.get<{ active_riders: Rider[]; users_minimal: User[]; groups?: any[] }>('/riders/admin', {
      timeout: 20000, // 20 second timeout for admin endpoint
    });
    return response.data;
  },

  // ============================================================================
  // Statistics API
  // Requirements: 5.1
  // ============================================================================

  /**
   * Get leaderboard data
   */
  async getLeaderboard(params: LeaderboardParams): Promise<LeaderboardPageResponse> {
    const response = await apiClient.get<LeaderboardPageResponse>('/leaderboard', { params });
    return response.data;
  },

  /**
   * Get statistics data for charts
   */
  async getStatistics(season: string): Promise<StatisticsData> {
    const response = await apiClient.get<StatisticsData>('/statistics', {
      params: { season },
    });
    return response.data;
  },

  /**
   * Get rider statistics for a specific season
   */
  async getRiderStats(riderId: string, season: string): Promise<RiderStats> {
    const response = await apiClient.get<RiderStats>(`/riders-stats/${riderId}`, {
      params: { season },
    });
    return response.data;
  },

  /**
   * Get dashboard summary data (stable data)
   * 
   * Uses stable cache with 30-minute TTL to improve performance
   * Concurrent requests are deduplicated to prevent multiple API calls
   * On API failure, falls back to stale stable cache
   */
  async getDashboard(): Promise<any> {
    // Check stable cache first
    const cached = getStableCacheFromStorage();
    const cacheTimestamp = getStableCacheTimestamp();
    
    if (cached && cacheTimestamp) {
      const age = Date.now() - cacheTimestamp;
      if (age < STABLE_CACHE_TTL) {
        console.log('[API] Returning cached stable data (age:', Math.round(age / 1000), 'seconds)');
        return cached;
      }
    }
    
    // If already fetching, return the existing promise
    if (dashboardFetchPromise) {
      console.log('[API] Dashboard fetch already in progress, waiting...');
      return dashboardFetchPromise;
    }
    
    // Fetch fresh data
    console.log('[API] Fetching fresh dashboard data from API');
    dashboardFetchPromise = apiClient.get('/dashboard')
      .then(response => {
        const data = response.data;
        setStableCache(data);
        dashboardFetchPromise = null;
        return data;
      })
      .catch(error => {
        dashboardFetchPromise = null;
        // Fall back to stale stable cache on API failure
        const staleData = getStaleStableCache();
        if (staleData) {
          console.warn('[API] Dashboard API failed, returning stale stable cache');
          return staleData;
        }
        throw error;
      });
    
    return dashboardFetchPromise;
  },

  /**
   * Get fresh RSVP data for dashboard events
   * 
   * Always fetches from API (never cached in localStorage)
   * Concurrent requests are deduplicated via rsvpFetchPromise
   */
  async getDashboardRsvp(): Promise<{ rsvp_events: Array<{ event_id: string; event_expectedAttendance: any; event_status: string }> }> {
    if (rsvpFetchPromise) return rsvpFetchPromise;
    rsvpFetchPromise = apiClient.get('/dashboard', { params: { rsvp_only: 'true' } })
      .then(response => { rsvpFetchPromise = null; return response.data; })
      .catch(error => { rsvpFetchPromise = null; throw error; });
    return rsvpFetchPromise;
  },

  /**
   * Get a specific dashboard section (lazy-loaded data).
   * 
   * Used for on-demand fetching of non-critical data like rider_info.
   * Respects Cache-Control headers from the Lambda (does not override).
   * Requirements: 10.2, 8.1, 8.2, 8.4
   */
  async getDashboardSection(section: string): Promise<any> {
    const response = await apiClient.get('/dashboard', { params: { section } });
    return response.data;
  },

  // ============================================================================
  // Admin - Users API
  // Requirements: 7.1
  // ============================================================================

  /**
   * Get all users (admin only)
   * 
   * Uses /users/admin endpoint which returns {users: [...], riders: [...]}
   * Results are cached for 5 minutes to improve performance
   * Concurrent requests are deduplicated to prevent multiple API calls
   */
  async getUsers(): Promise<User[]> {
    const usersCache = getUsersFromCache();
    const usersCacheTimestamp = getCacheTimestamp();
    
    console.log('[API] getUsers called. Cache status:', {
      hasCache: !!usersCache,
      cacheSize: usersCache?.length || 0,
      cacheTimestamp: usersCacheTimestamp,
      cacheAge: usersCacheTimestamp ? Date.now() - usersCacheTimestamp : null,
      hasFetchPromise: !!usersFetchPromise
    });

    // Check cache first
    if (usersCache && usersCacheTimestamp) {
      const cacheAge = Date.now() - usersCacheTimestamp;
      if (cacheAge < USERS_CACHE_TTL) {
        console.log('[API] Returning cached users list:', usersCache.length, 'users');
        return usersCache;
      } else {
        console.log('[API] Cache expired, age:', cacheAge, 'ms');
      }
    }

    // If a fetch is already in progress, return that promise
    if (usersFetchPromise) {
      console.log('[API] Reusing in-flight users request');
      return usersFetchPromise;
    }

    // Start new fetch
    console.log('[API] Starting new users fetch');
    usersFetchPromise = (async () => {
      try {
        const response = await apiClient.get<{ users: any[]; riders?: any[] }>('/users/admin', {
          timeout: 20000, // 20 second timeout for admin endpoint (Lambda takes ~14s)
        });
        
        // Admin endpoint returns {users: [...], riders: [...]}
        if ('users' in response.data && Array.isArray(response.data.users)) {
          // Convert backend format to frontend format
          const users = response.data.users.map((u: any) => ({
            username: u.username,
            email: u.email,
            name: u.name,
            phone: u.phone,
            groups: u.is_admin 
              ? (u.is_coach ? ['admin', 'coach'] : ['admin'])
              : (u.is_coach ? ['coach'] : []),
            last_login: u.last_login,
            rider_associations: u.rider_associations || [],
            rider_display: u.rider_display,
          }));
          
          // Cache the results in localStorage
          setUsersCache(users, response.data.riders || []);
          console.log('[API] Users cached:', users.length, 'users at timestamp', Date.now());
          
          return users;
        }
        
        // Fallback: empty array
        return [];
      } catch (error) {
        console.error('[API] Failed to fetch users:', error);
        throw new Error('Failed to load users');
      } finally {
        // Clear the promise so future requests can try again
        usersFetchPromise = null;
      }
    })();

    return usersFetchPromise;
  },

  /**
   * Get the timestamp of the users cache for visibility-change age check
   */
  getUsersCacheTimestamp(): number | null {
    return getCacheTimestamp();
  },

  /**
   * Get users and riders from a single /users/admin response
   * Returns { users: User[], riders: any[] }
   * Caches both users and riders in localStorage
   * Reuses existing deduplication logic (usersFetchPromise)
   * Requirements: 3.1, 3.2
   */
  async getUsersWithRiders(): Promise<{ users: User[]; riders: any[] }> {
    const usersCache = getUsersFromCache();
    const usersCacheTimestamp = getCacheTimestamp();

    // Check cache first — return both users and riders from cache
    if (usersCache && usersCacheTimestamp) {
      const cacheAge = Date.now() - usersCacheTimestamp;
      if (cacheAge < USERS_CACHE_TTL) {
        let ridersCache: any[] = [];
        try {
          const storage = getStorage();
          const ridersRaw = storage.getItem(USERS_RIDERS_CACHE_KEY);
          ridersCache = ridersRaw ? JSON.parse(ridersRaw) : [];
        } catch {
          ridersCache = [];
        }
        console.log('[API] Returning cached users+riders:', usersCache.length, 'users,', ridersCache.length, 'riders');
        return { users: usersCache, riders: ridersCache };
      }
    }

    // Reuse in-flight request if one exists
    if (usersFetchPromise) {
      console.log('[API] Reusing in-flight users request for getUsersWithRiders');
      const users = await usersFetchPromise;
      let ridersCache: any[] = [];
      try {
        const storage = getStorage();
        const ridersRaw = storage.getItem(USERS_RIDERS_CACHE_KEY);
        ridersCache = ridersRaw ? JSON.parse(ridersRaw) : [];
      } catch {
        ridersCache = [];
      }
      return { users, riders: ridersCache };
    }

    // Start new fetch — this populates both users and riders cache via setUsersCache
    const users = await this.getUsers();
    let ridersCache: any[] = [];
    try {
      const storage = getStorage();
      const ridersRaw = storage.getItem(USERS_RIDERS_CACHE_KEY);
      ridersCache = ridersRaw ? JSON.parse(ridersRaw) : [];
    } catch {
      ridersCache = [];
    }
    return { users, riders: ridersCache };
  },

  /**
   * Get current user info
   * 
   * Extracts user information directly from JWT token (like legacy app)
   * This avoids the timeout issue with the /auth/user endpoint for admin/coach users
   */
  async getCurrentUser(): Promise<User> {
    const tokens = getStoredTokens();
    if (!tokens) {
      throw new Error('No authentication tokens found');
    }
    
    try {
      // Decode token to get user info (same approach as legacy frontend)
      const payload = tokens.idToken.split('.')[1];
      const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
      const tokenData = JSON.parse(atob(paddedPayload));
      
      // Extract groups from token
      const groups = tokenData['cognito:groups'] || [];
      
      // Return user info from token
      return {
        username: tokenData['cognito:username'],
        email: tokenData['email'],
        name: tokenData['name'] || tokenData['given_name'],
        given_name: tokenData['given_name'],
        phone: tokenData['phone_number'],
        groups: Array.isArray(groups) ? groups : []
      };
    } catch (error) {
      console.error('[API] Failed to extract user from token:', error);
      throw new Error('Failed to load user profile');
    }
  },

  /**
   * Create a new user (admin only)
   */
  async createUser(user: CreateUserDto): Promise<User> {
    const response = await apiClient.post<User>('/users', user);
    clearUsersCache(); // Invalidate cache after creating
    return response.data;
  },

  /**
   * Get minimal user list (accessible to coaches and admins).
   * Returns [{username, email, name}] — used for name resolution.
   */
  async getUsersMinimal(): Promise<Array<{ username: string; email: string; name: string }>> {
    const response = await apiClient.get<Array<{ username: string; email: string; name: string }>>('/users', {
      params: { minimal: 'true' },
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * Update an existing user
   * Note: Uses /users endpoint (not /users/{username}) to match legacy API
   */
  async updateUser(username: string, user: UpdateUserDto): Promise<User> {
    // Transform groups array to is_admin/is_coach booleans for backend
    const payload: any = {
      ...user,
      email: user.username || username
    };
    
    // Convert groups array to is_admin/is_coach booleans
    if (user.groups !== undefined) {
      payload.is_admin = user.groups.includes('admin');
      payload.is_coach = user.groups.includes('coach');
      delete payload.groups; // Remove groups array from payload
    }
    
    const response = await apiClient.put<User>('/users', payload);
    clearUsersCache(); // Invalidate cache after updating
    return response.data;
  },

  /**
   * Delete a user (admin only)
   */
  async deleteUser(username: string): Promise<void> {
    await apiClient.delete('/users', {
      data: { email: username }
    });
    clearUsersCache(); // Invalidate cache after deleting
  },

  // ============================================================================
  // Admin - Password Management API
  // Requirements: Phase 3
  // ============================================================================

  /**
   * Send password reset email to user (Cognito forgot password flow)
   * Admin only
   * 
   * @param username - User's email/username
   * @returns Promise with success message
   */
  async sendPasswordResetEmail(username: string): Promise<{ message: string }> {
    console.log(`[API] sendPasswordResetEmail called for: ${username}`);
    const response = await apiClient.post<{ message: string }>(
      `/users/${encodeURIComponent(username)}/forgot-password`
    );
    console.log('[API] Password reset email sent successfully');
    return response.data;
  },

  /**
   * Set user password directly (admin only)
   * 
   * @param username - User's email/username
   * @param password - New password (must meet Cognito requirements)
   * @param permanent - If true, password is permanent; if false, user must change at next login
   * @returns Promise with success message
   */
  async setUserPassword(
    username: string,
    password: string,
    permanent: boolean = true
  ): Promise<{ message: string }> {
    console.log(`[API] setUserPassword called for: ${username}, permanent: ${permanent}`);
    const response = await apiClient.put<{ message: string }>(
      '/users/reset-password',
      {
        email: username,
        password,
        permanent,
      }
    );
    console.log('[API] Password set successfully');
    return response.data;
  },

  /**
   * Force user to change password at next login (admin only)
   * 
   * @param username - User's email/username
   * @returns Promise with success message
   */
  async forcePasswordReset(username: string): Promise<{ message: string }> {
    console.log(`[API] forcePasswordReset called for: ${username}`);
    const response = await apiClient.post<{ message: string }>(
      '/users/force-password-reset',
      {
        email: username,
      }
    );
    console.log('[API] Force password reset successful');
    return response.data;
  },

  // ============================================================================
  // Admin - Configuration API
  // Requirements: 8.1
  // ============================================================================

  /**
   * Get all event types
   */
  async getEventTypes(): Promise<EventType[]> {
    console.log('[API] getEventTypes() called');
    const response = await apiClient.get<any[]>('/eventTypes');
    console.log('[API] getEventTypes() raw response:', response.data);
    
    // Transform API response to match frontend interface
    const transformed = response.data.map((item: any) => ({
      type_id: item.eventType_id,
      name: item.eventType_name,
      description: item.eventType_description || item.description,
      color: item.eventType_color || item.color || '#435d7d',
    }));
    
    console.log('[API] getEventTypes() transformed:', transformed);
    return transformed;
  },

  /**
   * Create event type (admin only)
   * 
   * Backend now supports name, color, and description.
   */
  async createEventType(eventType: Omit<EventType, 'type_id'>): Promise<EventType> {
    // Generate UUID for new event type
    const eventType_id = crypto.randomUUID();
    
    // Transform frontend format to backend format
    const backendData: any = {
      eventType_id: eventType_id,
      eventType_name: eventType.name,
    };
    
    // Add optional fields if provided
    if (eventType.color) {
      backendData.color = eventType.color;
    }
    
    if (eventType.description) {
      backendData.description = eventType.description;
    }
    
    console.log('[API] createEventType() sending:', backendData);
    
    await apiClient.post<any>('/eventTypes', backendData);
    
    // Return created event type
    return {
      type_id: eventType_id,
      name: eventType.name,
      description: eventType.description,
      color: eventType.color,
    };
  },

  /**
   * Update event type (admin only)
   * 
   * Backend now supports updating name, color, and description.
   */
  async updateEventType(id: string, eventType: Partial<EventType>): Promise<EventType> {
    // Transform frontend format to backend format
    const backendData: any = {
      eventType_id: id,
      eventType_name: eventType.name || '',
    };
    
    // Add optional fields if provided
    if (eventType.color) {
      backendData.color = eventType.color;
    }
    
    if (eventType.description) {
      backendData.description = eventType.description;
    }
    
    console.log('[API] updateEventType() sending:', backendData);
    
    // Use PUT /eventTypes (not /eventTypes/{id}) with ID in body
    await apiClient.put<any>('/eventTypes', backendData);
    
    // Return updated event type
    return {
      type_id: id,
      name: eventType.name || '',
      description: eventType.description,
      color: eventType.color,
    };
  },

  /**
   * Delete event type (admin only)
   */
  async deleteEventType(id: string): Promise<void> {
    // Backend expects eventType_id in request body, not in URL path
    const backendData = {
      eventType_id: id,
    };
    
    console.log('[API] deleteEventType() sending:', backendData);
    
    await apiClient.delete('/eventTypes', { data: backendData });
  },

  /**
   * Get all locations
   */
  async getLocations(): Promise<Location[]> {
    console.log('[API] getLocations() called');
    const response = await apiClient.get<any[]>('/locations');
    console.log('[API] getLocations() raw response:', response.data);
    
    // Transform API response to match frontend interface
    const transformed = response.data.map((item: any) => ({
      location_id: item.location_id,
      name: item.location_name,
      address: item.location_address,
    }));
    
    console.log('[API] getLocations() transformed:', transformed);
    return transformed;
  },

  /**
   * Create location (admin only)
   */
  async createLocation(location: Omit<Location, 'location_id'>): Promise<Location> {
    // Generate UUID for new location
    const location_id = crypto.randomUUID();
    
    // Transform frontend format to backend format
    const backendData = {
      location_id: location_id,
      location_name: location.name,
      location_address: location.address || '',
    };
    
    console.log('[API] createLocation() sending:', backendData);
    
    await apiClient.post<any>('/locations', backendData);
    
    // Transform response back to frontend format
    return {
      location_id: location_id,
      name: location.name,
      address: location.address,
    };
  },

  /**
   * Update location (admin only)
   */
  async updateLocation(id: string, location: Partial<Location>): Promise<Location> {
    // Transform frontend format to backend format
    const backendData: any = {
      location_id: id,
    };
    
    if (location.name !== undefined) {
      backendData.location_name = location.name;
    }
    if (location.address !== undefined) {
      backendData.location_address = location.address;
    }
    
    console.log('[API] updateLocation() sending:', backendData);
    
    // Use PUT /locations (not /locations/{id}) with ID in body
    await apiClient.put<any>('/locations', backendData);
    
    // Transform response back to frontend format
    return {
      location_id: id,
      name: location.name || '',
      address: location.address,
    };
  },

  /**
   * Delete location (admin only)
   */
  async deleteLocation(id: string): Promise<void> {
    // Backend expects location_id in request body, not in URL path
    const backendData = {
      location_id: id,
    };
    
    console.log('[API] deleteLocation() sending:', backendData);
    
    await apiClient.delete('/locations', { data: backendData });
  },

  /**
   * Get all groups
   */
  async getGroups(): Promise<Group[]> {
    console.log('[API] getGroups() called');
    const response = await apiClient.get<any[]>('/groups');
    console.log('[API] getGroups() raw response:', response.data);
    
    // Transform API response to match frontend interface
    const transformed = response.data.map((item: any) => ({
      group_id: item.group_id,
      name: item.group_name,
      description: item.group_description || item.description,
      order: item.group_order || 999,
      group_leader: item.group_leader || '',
      group_skill_level: item.group_skill_level || '',
    }));
    
    console.log('[API] getGroups() transformed:', transformed);
    return transformed;
  },

  /**
   * Get check-in data (aggregated endpoint)
   * Returns current/next practice, riders, and groups in a single call
   * 
   * Requirements: Phase 2 optimization - reduces 3 API calls to 1
   */
  async getCheckinData(eventId?: string): Promise<CheckinPageData> {
    console.log('[API] getCheckinData() called', eventId ? { eventId } : {});
    const response = await apiClient.get<CheckinPageData>(
      '/checkin',
      eventId ? { params: { event_id: eventId } } : undefined
    );
    console.log('[API] getCheckinData() response:', {
      has_practice: response.data.current_practice !== null,
      practice_name: response.data.current_practice?.event_name,
      rider_count: response.data.riders.length,
      group_count: response.data.groups.length
    });
    
    // Transform groups to match frontend interface
    const transformedGroups = response.data.groups.map((item: any) => ({
      group_id: item.group_id,
      name: item.group_name || item.name,
      description: item.group_description || item.description,
      order: item.group_order || item.order || 999,
      group_leader: item.group_leader || '',
      group_skill_level: item.group_skill_level || '',
    }));
    
    return {
      current_practice: response.data.current_practice,
      riders: response.data.riders,
      groups: transformedGroups
    };
  },

  /**
   * Create group (admin only)
   */
  async createGroup(group: Omit<Group, 'group_id'>): Promise<Group> {
    // Generate UUID for new group
    const group_id = crypto.randomUUID();
    
    // Transform frontend format to backend format
    const backendData: any = {
      group_id: group_id,
      group_name: group.name,
      group_order: group.order || 0,
      group_leader: group.group_leader || '',
      group_skill_level: group.group_skill_level || '',
    };
    
    if (group.description) {
      backendData.group_description = group.description;
    }
    
    console.log('[API] createGroup() sending:', backendData);
    
    await apiClient.post<any>('/groups', backendData);
    
    // Transform response back to frontend format
    return {
      group_id: group_id,
      name: group.name,
      description: group.description,
      order: group.order || 0,
      group_leader: group.group_leader || '',
      group_skill_level: group.group_skill_level || '',
    };
  },

  /**
   * Update group (admin only)
   */
  async updateGroup(id: string, group: Partial<Group>): Promise<Group> {
    // Transform frontend format to backend format
    const backendData: any = {
      group_id: id,
    };
    
    if (group.name !== undefined) {
      backendData.group_name = group.name;
    }
    if (group.order !== undefined) {
      backendData.group_order = group.order;
    }
    if (group.description !== undefined) {
      backendData.group_description = group.description;
    }
    if (group.group_leader !== undefined) {
      backendData.group_leader = group.group_leader;
    }
    if (group.group_skill_level !== undefined) {
      backendData.group_skill_level = group.group_skill_level;
    }
    
    console.log('[API] updateGroup() sending:', backendData);
    
    // Use PUT /groups (not /groups/{id}) with ID in body
    await apiClient.put<any>('/groups', backendData);
    
    // Transform response back to frontend format
    return {
      group_id: id,
      name: group.name || '',
      description: group.description,
      order: group.order || 0,
      group_leader: group.group_leader || '',
      group_skill_level: group.group_skill_level || '',
    };
  },

  /**
   * Delete group (admin only)
   */
  async deleteGroup(id: string): Promise<void> {
    // Backend expects group_id in request body, not in URL path
    const backendData = {
      group_id: id,
    };
    
    console.log('[API] deleteGroup() sending:', backendData);
    
    await apiClient.delete('/groups', { data: backendData });
  },

  // ============================================================================
  // Admin - Trail Configurations API
  // Requirements: 3.1, 3.2, 3.3, 3.4
  // ============================================================================

  /**
   * Get all active trail configurations
   */
  async getTrailConfigurations(): Promise<TrailConfiguration[]> {
    console.log('[API] getTrailConfigurations() called');
    const response = await apiClient.get<{ trail_configurations: TrailConfiguration[] }>('/trail-configurations');
    console.log('[API] getTrailConfigurations() response:', response.data);
    return response.data.trail_configurations || [];
  },

  /**
   * Create trail configuration (admin only)
   */
  async createTrailConfiguration(data: Omit<TrailConfiguration, 'trail_id' | 'is_active' | 'created_at' | 'updated_at'>): Promise<TrailConfiguration> {
    console.log('[API] createTrailConfiguration() sending:', data);
    const response = await apiClient.post<TrailConfiguration>('/trail-configurations', data);
    console.log('[API] createTrailConfiguration() response:', response.data);
    return response.data;
  },

  /**
   * Update trail configuration (admin only)
   */
  async updateTrailConfiguration(id: string, data: Omit<TrailConfiguration, 'trail_id' | 'is_active' | 'created_at' | 'updated_at'>): Promise<TrailConfiguration> {
    console.log('[API] updateTrailConfiguration() sending:', { id, data });
    const response = await apiClient.put<TrailConfiguration>(`/trail-configurations/${id}`, data);
    console.log('[API] updateTrailConfiguration() response:', response.data);
    return response.data;
  },

  /**
   * Delete trail configuration (admin only)
   */
  async deleteTrailConfiguration(id: string): Promise<void> {
    console.log('[API] deleteTrailConfiguration() called with id:', id);
    await apiClient.delete(`/trail-configurations/${id}`);
    console.log('[API] deleteTrailConfiguration() completed');
  },

  // ============================================================================
  // Admin - Season Management API
  // Requirements: 9.1
  // ============================================================================

  /**
   * Get current season configuration
   */
  async getSeasonConfig(): Promise<{ currentSeason: string; availableSeasons: string[] }> {
    const response = await apiClient.get('/season-config');
    // API returns: { current_season, available_seasons, season_stats }
    return {
      currentSeason: response.data.current_season,
      availableSeasons: response.data.available_seasons
    };
  },

  /**
   * Initiate season rollover (admin only)
   */
  async rolloverSeason(newSeason: string, riderIds: string[] = []): Promise<{ success: number; errors: number }> {
    const response = await apiClient.post('/season-rollover', { 
      new_season: newSeason,
      rider_ids: riderIds,
      update_fields: {}
    });
    return response.data;
  },

  // ============================================================================
  // Cache Management
  // ============================================================================

  /**
   * Clear users cache to force fresh data fetch
   * Use this after operations that modify user-rider associations
   */
  clearUsersCache(): void {
    clearUsersCache();
  },

  /**
   * Get stale dashboard cache data regardless of TTL expiry.
   * Used for stale-while-error fallback when the API call fails.
   * Only checks cache version, ignores timestamp.
   */
  getStaleDashboardCache(): any | null {
    return getStaleStableCache();
  },

  /**
   * Clear the client-side dashboard cache (both localStorage and sessionStorage).
   * Call after RSVP updates or other mutations that affect dashboard data.
   */
  clearDashboardCache(): void {
    clearStableCache();
  },

  // ============================================================================
  // Time Trials API
  // Requirements: 1.1, 1.2, 1.3, 1.4
  // ============================================================================

  /**
   * Get time trials with optional filters
   * 
   * @param params - Query parameters
   * @param params.event_id - Filter by event ID (for leaderboards)
   * @param params.rider_id - Filter by rider ID (for rider history)
   * @param params.season - Filter by season
   * @returns Promise<TimeTrial[]>
   */
  async getTimeTrials(params: {
    event_id?: string;
    rider_id?: string;
    season?: string;
  }): Promise<TimeTrial[]> {
    const cacheKey = getCacheKey(params);
    
    // Check in-memory cache
    const cached = getFromCache(timeTrialsCache, cacheKey);
    if (cached) return cached as TimeTrial[];
    
    // Deduplicate concurrent requests for the same params
    const inflight = timeTrialsInflight.get(cacheKey);
    if (inflight) return inflight as Promise<TimeTrial[]>;
    
    const queryParams = new URLSearchParams();
    if (params.event_id) queryParams.append('event_id', params.event_id);
    if (params.rider_id) queryParams.append('rider_id', params.rider_id);
    if (params.season) queryParams.append('season', params.season);
    
    const promise = apiClient.get<any[]>(`/time-trials?${queryParams}`)
      .then(response => {
        const results = response.data.map((item: any) => ({
          trial_id: `${item.event_id}_${item.rider_id}`,
          rider_id: item.rider_id,
          event_id: item.event_id,
          time: item.recorded_time,
          date: item.timestamp,
          trail_name: item.trail_name,
          direction: item.direction,
          notes: item.notes,
          season: item.season
        }));
        timeTrialsCache.set(cacheKey, { data: results, timestamp: Date.now() });
        timeTrialsInflight.delete(cacheKey);
        return results;
      })
      .catch(err => {
        timeTrialsInflight.delete(cacheKey);
        throw err;
      });
    
    timeTrialsInflight.set(cacheKey, promise);
    return promise;
  },

  /**
   * Create a new time trial record
   * 
   * @param data - Time trial data
   * @returns Promise<TimeTrial>
   */
  async createTimeTrial(data: {
    event_id: string;
    rider_id: string;
    recorded_time: number;  // Time in seconds
    trail_name: string;
    direction: string;
    notes?: string;
  }): Promise<TimeTrial> {
    const response = await apiClient.post<any>('/time-trials', data);
    timeTrialsCache.clear(); // Invalidate cache after mutation
    
    // Transform backend format to frontend format
    const result = response.data.data || response.data;
    return {
      trial_id: `${result.event_id}_${result.rider_id}`,
      rider_id: result.rider_id,
      event_id: result.event_id,
      time: result.recorded_time,
      date: result.timestamp,
      trail_name: result.trail_name,
      direction: result.direction,
      notes: result.notes,
      season: result.season
    };
  },

  /**
   * Update an existing time trial record
   * 
   * @param data - Time trial update data (must include event_id and rider_id)
   * @returns Promise<void>
   */
  async updateTimeTrial(data: {
    event_id: string;
    rider_id: string;
    recorded_time?: number;  // Time in seconds
    trail_name?: string;
    direction?: string;
    notes?: string;
  }): Promise<void> {
    await apiClient.put('/time-trials', data);
    timeTrialsCache.clear(); // Invalidate cache after mutation
  },

  /**
   * Delete a time trial record
   * 
   * @param event_id - Event ID
   * @param rider_id - Rider ID
   * @returns Promise<void>
   */
  async deleteTimeTrial(event_id: string, rider_id: string): Promise<void> {
    await apiClient.delete(`/time-trials?event_id=${event_id}&rider_id=${rider_id}`);
    timeTrialsCache.clear(); // Invalidate cache after mutation
  },

  // ============================================================================
  // Rider Participation History API
  // ============================================================================

  /**
   * Get participation history for a rider
   * 
   * @param riderId - Rider ID
   * @param season - Optional season filter (year)
   * @returns Promise<ParticipationData>
   */
  async getRiderParticipation(
    riderId: string,
    season?: string
  ): Promise<import('../types/participation').ParticipationData> {
    const params = season ? { season } : {};
    const response = await apiClient.get(
      `/riders/${riderId}/participation`,
      { params }
    );
    return response.data;
  },

  // ============================================================================
  // Race Results API
  // Requirements: 2.1-2.8, 3.1-3.6, 4.1-4.5, 5.1-5.4
  // ============================================================================

  /**
   * Get race results with optional filters
   * 
   * @param params - Query parameters
   * @param params.event_id - Filter by event ID (for event leaderboards)
   * @param params.rider_id - Filter by rider ID (for rider history)
   * @param params.season - Filter by season
   * @returns Promise<RaceResult[]>
   */
  async getRaceResults(params: {
    event_id?: string;
    rider_id?: string;
    season?: string;
  }): Promise<import('../types/raceResults').RaceResult[]> {
    const cacheKey = getCacheKey(params);
    
    // Check in-memory cache
    const cached = getFromCache(raceResultsCache, cacheKey);
    if (cached) return cached as import('../types/raceResults').RaceResult[];
    
    // Deduplicate concurrent requests for the same params
    const inflight = raceResultsInflight.get(cacheKey);
    if (inflight) return inflight as Promise<import('../types/raceResults').RaceResult[]>;
    
    const queryParams = new URLSearchParams();
    if (params.event_id) queryParams.append('event_id', params.event_id);
    if (params.rider_id) queryParams.append('rider_id', params.rider_id);
    if (params.season) queryParams.append('season', params.season);
    
    const promise = apiClient.get<any[]>(`/race-results?${queryParams}`)
      .then(response => {
        const results = response.data.map((item: any) => ({
          event_id: item.event_id,
          rider_id: item.rider_id,
          event_name: item.event_name,
          event_starttime: item.event_starttime,
          position: Number(item.position),
          total_racers: item.total_racers ? Number(item.total_racers) : undefined,
          category: item.category,
          lap_time_1: item.lap_time_1 ? Number(item.lap_time_1) : undefined,
          lap_time_2: item.lap_time_2 ? Number(item.lap_time_2) : undefined,
          lap_time_3: item.lap_time_3 ? Number(item.lap_time_3) : undefined,
          lap_time_4: item.lap_time_4 ? Number(item.lap_time_4) : undefined,
          total_time: item.total_time ? Number(item.total_time) : undefined,
          dnf: item.dnf || undefined,
          notes: item.notes,
          timestamp: item.timestamp,
          season: item.season,
          recorded_by: item.recorded_by,
        }));
        raceResultsCache.set(cacheKey, { data: results, timestamp: Date.now() });
        raceResultsInflight.delete(cacheKey);
        return results;
      })
      .catch(err => {
        raceResultsInflight.delete(cacheKey);
        throw err;
      });
    
    raceResultsInflight.set(cacheKey, promise);
    return promise;
  },

  /**
   * Create a new race result record
   * 
   * @param data - Race result data
   * @returns Promise<RaceResult>
   */
  async createRaceResult(data: {
    event_id: string;
    rider_id: string;
    position: number;
    total_racers?: number;
    category?: string;
    lap_time_1?: number;  // Time in seconds
    lap_time_2?: number;  // Time in seconds
    lap_time_3?: number;  // Time in seconds
    lap_time_4?: number;  // Time in seconds
    total_time?: number;  // Time in seconds
    dnf?: boolean;        // Did Not Finish flag
    notes?: string;
  }): Promise<import('../types/raceResults').RaceResult> {
    const response = await apiClient.post<any>('/race-results', data);
    raceResultsCache.clear(); // Invalidate cache after mutation
    
    // Transform backend response to frontend format
    const result = response.data.data || response.data;
    return {
      event_id: result.event_id,
      rider_id: result.rider_id,
      position: Number(result.position),
      total_racers: result.total_racers ? Number(result.total_racers) : undefined,
      category: result.category,
      lap_time_1: result.lap_time_1 ? Number(result.lap_time_1) : undefined,
      lap_time_2: result.lap_time_2 ? Number(result.lap_time_2) : undefined,
      lap_time_3: result.lap_time_3 ? Number(result.lap_time_3) : undefined,
      lap_time_4: result.lap_time_4 ? Number(result.lap_time_4) : undefined,
      total_time: result.total_time ? Number(result.total_time) : undefined,
      dnf: result.dnf || undefined,
      notes: result.notes,
      timestamp: result.timestamp,
      season: result.season,
      recorded_by: result.recorded_by,
    };
  },

  /**
   * Update an existing race result record
   * 
   * @param data - Race result update data (must include event_id and rider_id)
   * @returns Promise<void>
   */
  async updateRaceResult(data: {
    event_id: string;
    rider_id: string;
    position?: number;
    total_racers?: number;
    category?: string;
    lap_time_1?: number;  // Time in seconds
    lap_time_2?: number;  // Time in seconds
    lap_time_3?: number;  // Time in seconds
    lap_time_4?: number;  // Time in seconds
    total_time?: number;  // Time in seconds
    dnf?: boolean;        // Did Not Finish flag
    notes?: string;
  }): Promise<void> {
    await apiClient.put('/race-results', data);
    raceResultsCache.clear(); // Invalidate cache after mutation
  },

  /**
   * Delete a race result record
   * 
   * @param event_id - Event ID
   * @param rider_id - Rider ID
   * @returns Promise<void>
   */
  async deleteRaceResult(event_id: string, rider_id: string): Promise<void> {
    await apiClient.delete(`/race-results?event_id=${event_id}&rider_id=${rider_id}`);
    raceResultsCache.clear(); // Invalidate cache after mutation
  },

  // ============================================================================
  // Incidents API
  // Requirements: 2.1, 8.2, 10.1, 13.5, 18.2
  // Note: Incidents are NOT cached — the list is expected to be refetched on
  // each page load and explicitly after a successful POST (see design.md
  // §Components and Interfaces → "No cache").
  // ============================================================================

  /**
   * Get all incidents for a given event, ordered newest-first by the backend.
   *
   * @param eventId - Event ID to filter incidents by
   * @returns Promise resolving to the list of incidents for the event
   * Requirements: 2.1
   */
  async getIncidentsByEvent(eventId: string): Promise<Incident[]> {
    const response = await apiClient.get<Incident[]>('/practice-incidents', {
      params: { event_id: eventId },
    });
    return response.data;
  },

  /**
   * Get all incidents across all events (admin/coach aggregate view).
   *
   * @returns Promise resolving to the full list of incidents
   * Requirements: 10.1
   */
  async getAllIncidents(): Promise<Incident[]> {
    const response = await apiClient.get<Incident[]>('/practice-incidents');
    return response.data;
  },

  /**
   * Get all incidents reported against a single rider, ordered newest-first
   * by the backend.
   *
   * The backend (`practice-incidents-get.py`) enforces `require_coach_or_admin`
   * on this endpoint — regular users (including a rider's own
   * `associatedUsers`) receive HTTP 403. Callers MUST guard this method
   * behind a role check; do not invoke it for non-privileged users.
   *
   * @param riderId - Rider ID to filter incidents by
   * @returns Promise resolving to the list of incidents where this rider
   *   was the subject, enriched with `event_name` / `event_date` /
   *   `recorded_by_display_name` for display convenience.
   * Requirements: rider detail incident history (post-launch enhancement)
   */
  async getIncidentsByRider(riderId: string): Promise<Incident[]> {
    const response = await apiClient.get<Incident[]>('/practice-incidents', {
      params: { rider_id: riderId },
    });
    return response.data;
  },

  /**
   * Create a new incident record for a practice event.
   *
   * The backend derives `recorded_by` and `recorded_by_display_name` from the
   * Cognito JWT on the server side; do NOT add a `recorded_by` field to the
   * request body — the backend returns HTTP 400 if it is present (Req 13.5).
   *
   * @param req - Incident creation payload (event_id, rider_id, incident_type, description)
   * @returns Promise resolving to the API's success envelope containing the created incident
   * Requirements: 8.2, 13.5
   */
  async createIncident(
    req: CreateIncidentRequest
  ): Promise<{ message: string; incident: Incident }> {
    // NOTE: `recorded_by` is intentionally omitted from the request body.
    // The backend derives it from the Cognito JWT; sending it yields HTTP 400.
    const response = await apiClient.post<{ message: string; incident: Incident }>(
      '/practice-incidents',
      req
    );
    return response.data;
  },

  /**
   * Update an existing incident record (admin-only — post-launch enhancement).
   *
   * The backend role gate (`practice-incidents-update.py`) rejects coaches
   * with HTTP 403 even if the UI somehow renders an Edit control to them;
   * do not rely on frontend-only gating. The backend sets `updated_by` and
   * `updated_by_display_name` server-side from the JWT and returns the
   * updated item in the response's `incident` envelope field.
   *
   * @param payload - UpdateIncidentRequest with required `incident_id` and
   *   any optional subset of `{rider_id, incident_type, description}` to change.
   * @returns Promise resolving to the updated incident record.
   * Requirements: admin-only edit (post-launch enhancement)
   */
  async updateIncident(payload: UpdateIncidentRequest): Promise<Incident> {
    // The PUT Lambda reads `incident_id` from the JSON body, so we do NOT
    // put it in the URL or query string — that would be a second source of
    // truth and could drift. One path, one identifier, no ambiguity.
    const response = await apiClient.put<{ message: string; incident: Incident }>(
      '/practice-incidents',
      payload
    );
    return response.data.incident;
  },

  /**
   * Delete an existing incident record (admin-only — post-launch enhancement).
   *
   * The backend role gate (`practice-incidents-delete.py`) rejects coaches
   * with HTTP 403. The backend `practice-incidents-delete.py` Lambda
   * accepts the incident id in path params, the query string, or the body;
   * we use a query param here to match the DELETE convention already used
   * for time-trials and race-results in this file.
   *
   * @param incidentId - The UUID of the incident to delete.
   * Requirements: admin-only delete (post-launch enhancement)
   */
  async deleteIncident(incidentId: string): Promise<void> {
    await apiClient.delete('/practice-incidents', {
      params: { incident_id: incidentId },
    });
  },

  // ============================================================================
  // Skills Config API
  // Requirements: 7.1, 7.2, 7.7
  // ============================================================================

  /**
   * Get skills configuration, optionally filtered by group name
   * When groupName is omitted, returns all skills across all groups (admin use)
   */
  async getSkillsConfig(groupName?: string): Promise<SkillConfig[]> {
    const params = groupName ? { group_name: groupName } : {};
    const response = await apiClient.get<SkillConfig[]>('/skills-config', { params });
    return response.data;
  },

  /**
   * Create a new skill config entry (admin only)
   */
  async createSkillConfig(skill: CreateSkillConfigDto): Promise<SkillConfig> {
    const response = await apiClient.post<SkillConfig>('/skills-config', skill);
    return response.data;
  },

  /**
   * Update an existing skill config entry (admin only)
   * Uses skill_id + group_name as composite key in the body
   */
  async updateSkillConfig(skill: UpdateSkillConfigDto): Promise<SkillConfig> {
    const response = await apiClient.put<SkillConfig>('/skills-config', skill);
    return response.data;
  },

  /**
   * Delete a single skill config entry (admin only)
   * Backend expects skill_id and group_name in request body
   */
  async deleteSkillConfig(params: DeleteSkillConfigDto): Promise<void> {
    await apiClient.delete('/skills-config', { data: params });
  },

  /**
   * Delete all skills in a category for a group (admin only)
   * Backend expects group_name, section, and category in request body
   */
  async deleteSkillCategory(params: DeleteSkillCategoryDto): Promise<void> {
    await apiClient.delete('/skills-config', { data: params });
  },

  // ============================================================================
  // Skills Assessment API
  // Requirements: 7.3, 7.4
  // ============================================================================

  /**
   * Get all skill assessments for a rider
   */
  async getSkillsAssessments(riderId: string): Promise<SkillAssessment[]> {
    const response = await apiClient.get<SkillAssessment[]>('/skills-assessment', {
      params: { rider_id: riderId },
    });
    return response.data;
  },

  /**
   * Create a new skill assessment (coach/admin only)
   * The backend generates assessed_at timestamp and composes the sort key
   */
  async createSkillAssessment(assessment: CreateSkillAssessmentDto): Promise<SkillAssessment> {
    const response = await apiClient.post<SkillAssessment>('/skills-assessment', assessment);
    // Clear skills report cache so dashboard shows fresh data
    try {
      sessionStorage.removeItem('blueclipboard_skills_report_cache');
      sessionStorage.removeItem('blueclipboard_skills_report_cache_timestamp');
    } catch { /* ignore */ }
    return response.data;
  },

  // ============================================================================
  // Skills Seed API
  // Requirements: 7.2
  // ============================================================================

  /**
   * Seed initial skills data (admin only)
   * Returns count of seeded items, or message if skills already exist
   */
  async seedSkills(): Promise<{ count: number; message: string }> {
    const response = await apiClient.post<{ count: number; message: string }>('/skills-seed');
    return response.data;
  },

  // ============================================================================
  // Skills Threshold Settings API
  // Requirements: 1.2, 1.3
  // ============================================================================

  /**
   * Get current threshold settings for recommendation engine
   * Returns defaults if no settings have been configured
   */
  async getThresholdSettings(): Promise<ThresholdSettings> {
    const response = await apiClient.get<ThresholdSettings>('/skills-config/thresholds');
    return response.data;
  },

  /**
   * Update threshold settings for recommendation engine (admin only)
   */
  async updateThresholdSettings(settings: ThresholdSettings): Promise<ThresholdSettings> {
    const response = await apiClient.put<ThresholdSettings>('/skills-config/thresholds', settings);
    return response.data;
  },

  // ============================================================================
  // Time-Trial Zone API
  // Requirements: 4.1, 4.2
  // ============================================================================

  /**
   * Get time-trial zone assessment for a rider within their group
   */
  async getTimeTrialZone(riderId: string, group: string): Promise<TimeTrialZoneResponse> {
    const response = await apiClient.get<TimeTrialZoneResponse>('/time-trials/zone', {
      params: { rider_id: riderId, group },
    });
    return response.data;
  },

  // ============================================================================
  // Admin - RSVP Reminder Test API
  // Requirements: 9.1, 9.3, 9.6
  // ============================================================================

  /**
   * Trigger RSVP reminder test email (admin only)
   * Sends a test reminder email to the requesting admin user
   */
  async sendRsvpReminderTest(): Promise<{
    message: string;
    events_found: number;
    riders_identified: number;
    email_sent: boolean;
  }> {
    const response = await apiClient.post<{
      message: string;
      events_found: number;
      riders_identified: number;
      email_sent: boolean;
    }>('/admin/rsvp-reminder-test', { test_mode: true });
    return response.data;
  },

  // ============================================================================
  // Skills Report API
  // Requirements: 7.1
  // ============================================================================

  /**
   * Get the aggregated skills report with riders ranked by Skills Score within each group.
   * Pass a group name to fetch only that group's data (reduces response size).
   */
  async getSkillsReport(group?: string): Promise<SkillsReportResponse> {
    const params = group ? { group } : {};
    const response = await apiClient.get<SkillsReportResponse>('/skills-report', { params });
    return response.data;
  },

  // ============================================================================
  // Training Plans API
  // Per-week plans, one per skill level (Beginner/Intermediate/Advanced).
  // Displayed on the Dashboard and Skills Reference page; editable by admins.
  // ============================================================================

  /**
   * Get all training plans for a season plus the season's current week.
   * Season defaults (server-side) to the current UTC year when omitted.
   */
  async getTrainingPlans(season?: string): Promise<TrainingPlansResponse> {
    const params = season ? { season } : {};
    const response = await apiClient.get<TrainingPlansResponse>('/training-plans', { params });
    return response.data;
  },

  /**
   * Create a new training plan (admin only). Fails if a plan already exists
   * for the same (season, level, week).
   */
  async createTrainingPlan(plan: UpsertTrainingPlanDto): Promise<TrainingPlan> {
    const response = await apiClient.post<TrainingPlan>('/training-plans', plan);
    return response.data;
  },

  /**
   * Update (upsert) an existing training plan (admin only).
   */
  async updateTrainingPlan(plan: UpsertTrainingPlanDto): Promise<TrainingPlan> {
    const response = await apiClient.put<TrainingPlan>('/training-plans', plan);
    return response.data;
  },

  /**
   * Delete a training plan (admin only).
   */
  async deleteTrainingPlan(params: DeleteTrainingPlanDto): Promise<void> {
    await apiClient.delete('/training-plans', { data: params });
  },

  /**
   * Preview (dry-run) a level's import from CSV text (admin only). Validates
   * and parses the file and reports which existing weeks would be replaced,
   * WITHOUT writing anything.
   */
  async previewTrainingPlanImport(
    data: ImportTrainingPlansDto,
  ): Promise<TrainingPlanImportPreview> {
    const response = await apiClient.post<TrainingPlanImportPreview>('/training-plans-import', {
      ...data,
      dry_run: true,
    });
    return response.data;
  },

  /**
   * Bulk-import a level's weekly plans from CSV text (admin only). The backend
   * validates the whole file, groups rows by week, and upserts week items
   * (full-week replace per (season, level, week); weeks absent from the file
   * are left untouched).
   */
  async importTrainingPlans(
    data: ImportTrainingPlansDto,
  ): Promise<TrainingPlanImportResult> {
    const response = await apiClient.post<TrainingPlanImportResult>('/training-plans-import', {
      ...data,
      dry_run: false,
    });
    return response.data;
  },

  /**
   * Set a season's start and end dates (admin only). These drive the computed
   * current week surfaced on the dashboard and Training page.
   */
  async setTrainingSeasonDates(dates: TrainingSeasonDatesDto): Promise<{ season: string; start_date: string; end_date: string }> {
    const response = await apiClient.put<{ season: string; start_date: string; end_date: string }>('/training-plans-config', dates);
    return response.data;
  },

  // ============================================================================
  // MOTD (Message of the Day) API
  // Requirements: 5.1, 5.2, 5.3, 5.4, 6.6
  // ============================================================================

  /**
   * Get the current active MOTD
   */
  async getMotd(): Promise<{ message: string; version: string; updated_at: string; active: boolean }> {
    const response = await apiClient.get<{ message: string; version: string; updated_at: string; active: boolean }>('/motd');
    return response.data;
  },

  /**
   * Create or update the MOTD (admin only)
   */
  async putMotd(message: string): Promise<{ message: string; version: string; updated_at: string; active: boolean }> {
    const response = await apiClient.put<{ message: string; version: string; updated_at: string; active: boolean }>('/motd', { message });
    return response.data;
  },

  /**
   * Acknowledge the current MOTD
   */
  async acknowledgeMotd(): Promise<{ acknowledged: boolean }> {
    const response = await apiClient.post<{ acknowledged: boolean }>('/motd/acknowledge');
    return response.data;
  },

  /**
   * Check if the current user needs to acknowledge the MOTD
   */
  async getMotdStatus(): Promise<{ needs_acknowledgment: boolean; version?: string }> {
    const response = await apiClient.get<{ needs_acknowledgment: boolean; version?: string }>('/motd/status');
    return response.data;
  },

  /**
   * Get MOTD history (admin only)
   */
  async getMotdHistory(): Promise<{ history: Array<{ message: string; version: string; updated_by: string; updated_at: string }> }> {
    const response = await apiClient.get<{ history: Array<{ message: string; version: string; updated_by: string; updated_at: string }> }>('/motd/history');
    return response.data;
  },
};

export default restClient;
